import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App';
import { startSyncWorker } from './shared/offline/syncWorker';
import './index.css';

// Kick off the offline write drainer once at boot. Safe to call in dev — it
// no-ops if the browser is offline and installs a single `online` listener.
startSyncWorker();

// In development, evict any service worker a previous build installed.
//
// The dev server no longer registers one, but a worker already sitting in the
// browser keeps intercepting requests and answering them from its own cache —
// including API responses recorded when tables were still empty. The symptom
// is a screen that says there is no data while the API returns plenty, which
// is a genuinely hard thing to diagnose from the outside. Clearing it here
// means one refresh fixes it rather than a trip through DevTools.
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.getRegistrations()
    .then((regs) => Promise.all(regs.map((r) => r.unregister())))
    .then(async (unregistered) => {
      if (!unregistered.some(Boolean)) return;
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.filter((k) => k.startsWith('api-')).map((k) => caches.delete(k)));
      }
      // The page is still being served by the worker we just removed, so a
      // reload is what actually puts fresh responses on screen.
      window.location.reload();
    })
    .catch(() => {
      // Never let cache housekeeping stop the app from starting.
    });
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <StrictMode>
    <Suspense fallback={<div className="p-8 text-muted-foreground">Loading…</div>}>
      <App />
    </Suspense>
  </StrictMode>,
);
